import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import { ExpandMore, Lock } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { useAuth, AuthStatus } from '../auth/Auth';
import { useState } from 'react';
import SubscriptionPriceMatrix from '../subscription/SubscriptionPriceMatrix';

interface LockedContentOverlayProps {
    /** The content to render behind the blur */
    children: React.ReactNode;
    /** Whether the content should be locked */
    locked: boolean;
    /** Optional message to display */
    message?: string;
    /** Blur intensity in pixels (default 6) */
    blurAmount?: number;
}

/**
 * Wraps children in a blurred overlay with a subscribe CTA when locked.
 * When unlocked, renders children normally with no wrapper overhead.
 */
const LockedContentOverlay: React.FC<LockedContentOverlayProps> = ({
    children,
    locked,
    message = 'Subscribe to explore EnterStellar Spacebar and access exclusive content!',
    blurAmount = 6,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const auth = useAuth();
    const [showPricing, setShowPricing] = useState(false);

    if (!locked) {
        return <>{children}</>;
    }

    const isAuthenticated = auth.status === AuthStatus.Authenticated;
    const user = isAuthenticated ? auth.user : undefined;

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Blurred content */}
            <Box
                sx={{
                    filter: `blur(${blurAmount}px)`,
                    pointerEvents: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                }}
            >
                {children}
            </Box>

            {/* Overlay with CTA */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    borderRadius: 2,
                    zIndex: 1,
                    overflow: showPricing ? 'visible' : 'hidden',
                }}
            >
                <Stack
                    spacing={1.5}
                    alignItems='center'
                    sx={{
                        p: 2,
                        textAlign: 'center',
                        maxWidth: 600,
                        width: '100%',
                    }}
                >
                    <motion.div
                        animate={{ scale: [1, 1.12, 1] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                    >
                        <Lock
                            sx={{
                                fontSize: 36,
                                color: theme.palette.primary.main,
                                opacity: 0.9,
                                filter: `drop-shadow(0 0 8px ${theme.palette.primary.main}80)`,
                            }}
                        />
                    </motion.div>
                    <Typography
                        variant='body1'
                        sx={{
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                        }}
                    >
                        {message}
                    </Typography>

                    {!isAuthenticated ? (
                        <Button
                            variant='contained'
                            color='primary'
                            size='large'
                            onClick={() => navigate('/signin')}
                            sx={{
                                borderRadius: 3,
                                px: 4,
                                textTransform: 'none',
                                fontWeight: 'bold',
                            }}
                        >
                            Sign In to Subscribe
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant='outlined'
                                color='primary'
                                onClick={() => setShowPricing((prev) => !prev)}
                                endIcon={
                                    <ExpandMore
                                        sx={{
                                            transition: 'transform 0.2s',
                                            transform: showPricing
                                                ? 'rotate(180deg)'
                                                : 'rotate(0deg)',
                                        }}
                                    />
                                }
                                sx={{
                                    borderRadius: 3,
                                    px: 3,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                }}
                            >
                                {showPricing
                                    ? 'Hide Subscription Options'
                                    : 'View Subscription Options'}
                            </Button>
                            <Collapse in={showPricing} sx={{ width: '100%' }}>
                                <Box sx={{ pb: '100px' }}>
                                    <SubscriptionPriceMatrix
                                        user={user}
                                        redirectPath={location.pathname}
                                    />
                                </Box>
                            </Collapse>
                        </>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

export default LockedContentOverlay;
